import { copy } from "./copy.js";

/** Minimal shape needed to compute the report — matches the `orders` table row. */
export type ReportOrderInput = {
  status: string;
  reject_reason: string | null;
  amount: number | null;
  extra_sales: number | null;
  purchase_price: number | null;
  delivery_cost: number | null;
  car_brand: string | null;
};

export type MonthlyReportStats = {
  total: number;
  placed: number;
  placedPct: number;
  rejectedPrice: number;
  rejectedNotRelevant: number;
  revenue: number;
  extraSalesCount: number;
  extraSalesSum: number;
  cost: number;
  delivery: number;
  profit: number;
  topBrands: [string, number][];
};

/**
 * Pure computation shared by apps/web's ReportsPage, companion-bot-worker's /report command,
 * and scheduler-worker's automatic monthly send (AGENTS.md §6.9, §10) — one implementation,
 * three call sites, so the numbers can never drift between the UI and the bot.
 */
export function computeMonthlyReport(orders: ReportOrderInput[]): MonthlyReportStats {
  const total = orders.length;
  const rejected = orders.filter((o) => o.status === "rejected");
  const rejectedPrice = rejected.filter((o) => o.reject_reason === "price").length;
  const rejectedNotRelevant = rejected.filter((o) => o.reject_reason === "not_relevant").length;
  const placed = orders.filter((o) => o.status !== "rejected");
  const placedPct = total > 0 ? Math.round((placed.length / total) * 100) : 0;

  const revenue = placed.reduce((sum, o) => sum + Number(o.amount ?? 0), 0);
  const extraSalesCount = placed.filter((o) => Number(o.extra_sales ?? 0) > 0).length;
  const extraSalesSum = placed.reduce((sum, o) => sum + Number(o.extra_sales ?? 0), 0);
  const cost = placed.reduce((sum, o) => sum + Number(o.purchase_price ?? 0), 0);
  const delivery = placed.reduce((sum, o) => sum + Number(o.delivery_cost ?? 0), 0);
  const profit = revenue + extraSalesSum - cost - delivery;

  const brandCounts = new Map<string, number>();
  for (const o of placed) {
    if (!o.car_brand) continue;
    brandCounts.set(o.car_brand, (brandCounts.get(o.car_brand) ?? 0) + 1);
  }
  const topBrands = [...brandCounts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5);

  return {
    total,
    placed: placed.length,
    placedPct,
    rejectedPrice,
    rejectedNotRelevant,
    revenue,
    extraSalesCount,
    extraSalesSum,
    cost,
    delivery,
    profit,
    topBrands,
  };
}

export function formatMonthlyReportForTelegram(stats: MonthlyReportStats, monthLabel: string): string {
  const lines = [
    `<b>${copy.reports.title} — ${monthLabel}</b>`,
    "",
    `Опрацьовано заявок: ${stats.total} → ${copy.leadsCategory.placed}: ${stats.placed} (${stats.placedPct}%)`,
    `${copy.rejectReason.price}: ${stats.rejectedPrice} · ${copy.rejectReason.not_relevant}: ${stats.rejectedNotRelevant}`,
    "",
    `${copy.reports.revenue}: $${stats.revenue.toFixed(0)}`,
    `${copy.reports.extraSales}: ${stats.extraSalesCount} / $${stats.extraSalesSum.toFixed(0)}`,
    `${copy.reports.cost}: $${stats.cost.toFixed(0)}`,
    `${copy.reports.delivery}: $${stats.delivery.toFixed(0)}`,
    `${copy.reports.profit}: $${stats.profit.toFixed(0)}`,
  ];

  if (stats.topBrands.length > 0) {
    lines.push("", `<b>${copy.reports.topBrands}:</b>`);
    stats.topBrands.forEach(([brand, count], i) => lines.push(`${i + 1}. ${brand} — ${count}`));
  }

  return lines.join("\n");
}
